import React, { useState } from 'react'
import { Button, Card, Col, DatePicker, Form, Input, Modal, Row, Select } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChartLine,
  faFileCirclePlus,
  faMailReply,
  faMoneyBillTransfer,
  faMoneyCheck,
  faMoneyCheckDollar,
  faUserCircle,
  faUserPlus,
  faFileInvoiceDollar,
  faEye
} from '@fortawesome/free-solid-svg-icons'
import { handleDashboardCardClick, openNewInvoiceForm, openAddPatientForm } from '../utils'
import CoreEnums from '../../../../../core/utils/enums'
import dashboardStyles from '../styles/dashboardStyles'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../../../core/utils/reducer'
import PatientPaymentFormWrapper from '../../../components/patient-payment-form'
import MedicalAidPaymentForm from '../../../components/medical-aid-payment-form'
import dayjs from 'dayjs'
import AgiliteSkeleton from '../../../../reusable-components/AgiliteSkeleton'

const QuickActions = () => {
  const dispatch = useDispatch()
  const [remittanceForm] = Form.useForm()
  const [remittanceModalVisible, setRemittanceModalVisible] = useState(false)
  const [remittanceLoading, setRemittanceLoading] = useState(false)
  const [schemeData, setSchemeData] = useState([])
  const [schemeLoading, setSchemeLoading] = useState(false)

  // Function to open patient payment form
  const openPatientPaymentForm = () => {
    dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.PATIENT_PAYMENTS}_new`,
        closable: true,
        label: 'New Patient Payment',
        children: <PatientPaymentFormWrapper data={{}} />
      })
    )
  }

  // Function to handle saving remittance - similar to handleSaveRemittance in useMedicalAidPaymentList
  const handleSaveRemittance = async (values, form, callback) => {
    try {
      setRemittanceLoading(true)

      // Create API payload
      const payload = {
        ...values,
        remittanceDate: values.remittanceDate ? values.remittanceDate.format('YYYY-MM-DD') : null,
        createdAt: new Date()
      }

      // Here you would normally make an API call to save the remittance
      // For demonstration purposes, we'll simulate a response
      const remittanceData = {
        ...payload,
        _id: `temp_${Date.now()}`,
        total: parseFloat(payload.total || 0),
        runningTotal: parseFloat(payload.runningTotal || 0),
        finalized: false
      }

      setRemittanceLoading(false)
      setRemittanceModalVisible(false)
      form.resetFields()

      if (callback) callback()

      return remittanceData
    } catch (error) {
      console.error('Error saving remittance:', error)
      setRemittanceLoading(false)
      return null
    }
  }

  // Function to save and open payment form - similar to handleSaveAndOpenPaymentForm in RemittanceList
  const handleSaveAndOpenPaymentForm = async () => {
    try {
      await remittanceForm.validateFields()

      const remittanceData = await handleSaveRemittance(remittanceForm.getFieldsValue(), remittanceForm, null)

      if (remittanceData) {
        dispatch(
          coreReducer.actions.addTab({
            key: `${CoreEnums.tabKeys.PAYMENT_REMITTANCE}_new`,
            closable: true,
            label: 'Create New Medical Aid Payment',
            children: <MedicalAidPaymentForm data={remittanceData} handleGetRemittances={() => {}} />
          })
        )
      }
    } catch (error) {
      console.error('Validation error:', error)
    }
  }

  // Simulated debounced scheme filter function
  const debouncedSchemeFilter = (value) => {
    if (!value) {
      setSchemeData([])
      return
    }

    setSchemeLoading(true)

    // Simulate API call delay
    setTimeout(() => {
      // Mock data for demonstration
      const mockSchemes = [
        { name: 'Discovery Health' },
        { name: 'Bonitas' },
        { name: 'Momentum Health' },
        { name: 'Medshield' },
        { name: 'Bestmed' }
      ]

      const filteredSchemes = mockSchemes.filter((scheme) => scheme.name.toLowerCase().includes(value.toLowerCase()))

      setSchemeData(filteredSchemes)
      setSchemeLoading(false)
    }, 500)
  }

  // Quick action cards
  const quickActions = [
    {
      title: 'New Invoice',
      icon: <FontAwesomeIcon icon={faFileCirclePlus} />,
      color: '#1890ff',
      onClick: () => openNewInvoiceForm()
    },
    {
      title: 'View Invoices',
      icon: <FontAwesomeIcon icon={faEye} />,
      color: '#1890ff',
      onClick: () => handleDashboardCardClick(CoreEnums.tabKeys.INVOICES)
    },
    {
      title: 'New Remittance',
      icon: <FontAwesomeIcon icon={faMoneyCheck} />,
      color: '#fa8c16',
      onClick: () => setRemittanceModalVisible(true)
    },
    {
      title: 'New Quote',
      icon: <FontAwesomeIcon icon={faFileInvoiceDollar} />,
      color: '#13c2c2',
      onClick: () => handleDashboardCardClick(CoreEnums.tabKeys.QUOTES)
    },
    {
      title: 'Reports',
      icon: <FontAwesomeIcon icon={faChartLine} />,
      color: '#eb2f96',
      onClick: () => handleDashboardCardClick(CoreEnums.tabKeys.BILLING_REPORTS)
    },
    {
      title: 'New Patient',
      icon: <FontAwesomeIcon icon={faUserPlus} />,
      color: '#52c41a',
      onClick: () => openAddPatientForm()
    }
  ]

  return (
    <>
      <Card title='Quick Actions' style={dashboardStyles.card}>
        <Row gutter={[16, 16]}>
          {quickActions.map((action, index) => (
            <Col xs={24} sm={12} md={8} lg={4} key={index}>
              <Card
                hoverable
                onClick={action.onClick}
                style={{
                  ...dashboardStyles.actionCard,
                  borderTop: `2px solid ${action.color}`
                }}
              >
                <div
                  style={{
                    fontSize: '24px',
                    color: action.color,
                    marginBottom: '8px'
                  }}
                >
                  {action.icon}
                </div>
                <div>{action.title}</div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      <Modal
        title='Create Payment Remittance'
        closable={false}
        destroyOnClose={true}
        okText='Save'
        cancelButtonProps={{ danger: true }}
        okButtonProps={{ loading: remittanceLoading }}
        onCancel={() => {
          remittanceForm.resetFields()
          setRemittanceModalVisible(false)
        }}
        open={remittanceModalVisible}
        onOk={handleSaveAndOpenPaymentForm}
        width={800}
      >
        <Form
          layout='vertical'
          form={remittanceForm}
          initialValues={{
            receiptNumber: dayjs(new Date()).format('YYMMDDHHmmss')
          }}
        >
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <Form.Item
                name='medicalAid'
                label='Medical Aid'
                rules={[{ required: true, message: 'Medical Aid is required' }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder='Search for a Medical Aid...'
                  onSearch={(value) => {
                    debouncedSchemeFilter(value)
                  }}
                  onChange={(e, option) => {
                    remittanceForm.setFieldValue('medicalAid', option?.data ? JSON.parse(option.data).name : '')
                    setSchemeData([])
                  }}
                  options={schemeData.map((i) => {
                    return {
                      label: i.name,
                      value: i.name,
                      data: JSON.stringify(i)
                    }
                  })}
                  notFoundContent={
                    schemeLoading ? <AgiliteSkeleton skActive spinnerTip='Searching Schemes...' /> : undefined
                  }
                />
              </Form.Item>
              <Form.Item
                name='remittanceDate'
                label='Remittance Date'
                rules={[{ required: true, message: 'Remittance Date is required' }]}
              >
                <DatePicker style={{ width: '100%' }} placeholder='Remittance Date' />
              </Form.Item>
              <Form.Item
                name='receiptNumber'
                label='Receipt Number'
                rules={[{ required: true, message: 'Receipt Number is required' }]}
              >
                <Input readOnly style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='total' label='Total' rules={[{ required: true, message: 'Total is required' }]}>
                <Input
                  addonBefore='R'
                  style={{ width: '100%' }}
                  onChange={(e) => {
                    remittanceForm.setFieldValue('runningTotal', e.target.value)
                  }}
                />
              </Form.Item>
              <Form.Item
                name='runningTotal'
                label='Running Total'
                rules={[{ required: true, message: 'Running Total is required' }]}
              >
                <Input addonBefore='R' style={{ width: '100%' }} disabled />
              </Form.Item>
              <Form.Item name='practiceRefNo' label='Practice Reference Number'>
                <Input style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  )
}

export default QuickActions
